import { DateTime } from 'luxon';
import React from 'react';

import { customFrequencyUtils } from 'app-components/flux/scheduled-transactions';
import { categoriesSelectors } from 'app-components/flux/categories';
import { scheduledTransactionsUtils } from 'app-components/flux/scheduled-transactions';
import { transactionsUtils } from 'app-components/flux/transactions';
import type {
  ScheduledTransaction,
  ScheduledTransactionType,
} from 'app-components/flux/scheduled-transactions/scheduledTransactionsTypes';
import type { TransactionProps } from 'app-components/flux/transactions/transactionsTypes';
import { toPlainIfImmutable } from 'app-components/utils/core/immutableHelpers';

import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { getSmartDateLabel } from 'app-components/utils/date';
import { makeStyles } from 'tss-react/mui';

import BillPresentmentConnectBox from 'components/BillPresentmentConnectBox';
import ConnectedAvatar from 'components/ConnectedAvatar';
import { getSeriesIconKind } from 'images/icons/SeriesIcon';
// CUSTOM
import StdDialog from 'components/Dialogs/StdDialog';
import StdDialogTitle from 'components/Dialogs/StdDialogTitle';
import type { RootModalDrawerOptions } from 'components/Drawers/standardDrawerContract';
import QSkeleton from 'components/QSkeleton';
import AmountField, { ShowSignEnum } from 'components/QuickenControls/AmountField';
import { useAppSelector } from 'hooks/redux';

const useStyles = makeStyles()((theme) => ({
  root: {
    padding: '12px 0px 24px',
    height: 'fit-content',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative',
  },
  drawerContent: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    padding: '16px 0 24px',
  },
  summary: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
  },
  drawerSummary: {
    paddingTop: 32,
  },
  drawerConnect: {
    width: '100%',
    marginTop: 'auto',
  },
  drawerFooter: {
    flexShrink: 0,
    padding: 24,
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  drawerCloseButton: {
    width: '100%',
    height: 36,
  },
  name: {
    width: '100%',
    padding: '0 24px',
    margin: '8px 0 14px',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  dotRow: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: theme.palette.text.secondary,
  },
  amount: {
    fontWeight: 500,
    color: 'inherit',
  },
  frequency: {
    color: 'inherit',
  },
  dot: {
    height: 8,
    weight: 8,
  },
  editButton: {
    marginTop: 16,
    marginBottom: 12,
  },
  drawerEditButton: {
    marginTop: 16,
    marginBottom: 12,
    minWidth: 115,
    height: 42,
  },
}));

type ReviewDialogProps = {
  model?: ScheduledTransaction;
  open?: boolean;
  onClose?: () => void;
  edit?: (initialTab?: number) => void;
  rootModalDrawer?: RootModalDrawerOptions;
  sharedcomponentid?: string;
};

function CreatingSeriesLoading(): React.JSX.Element {
  return (
    <Box
      aria-label='Creating Series'
      role='status'
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', px: 3, pb: 3, pt: 2 }}
    >
      <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', alignItems: 'center', py: 8 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
          <Box sx={{ position: 'relative', height: 128, width: 128 }}>
            <CircularProgress
              aria-hidden
              color='primary'
              size={128}
              thickness={5.5}
              value={100}
              variant='determinate'
              sx={{ color: 'primary.light', position: 'absolute', inset: 0 }}
            />
            <CircularProgress
              aria-hidden
              color='primary'
              size={128}
              thickness={5.5}
              sx={{
                position: 'absolute',
                inset: 0,
                '& .MuiCircularProgress-circle': { strokeLinecap: 'round' },
              }}
            />
          </Box>
          <Typography color='text.secondary' textAlign='center' variant='headlineMedium'>
            Creating Series
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function ReviewDialog(props: ReviewDialogProps): React.JSX.Element | null {
  const { classes, cx } = useStyles();
  const { model, open = false, onClose = () => {}, edit = () => {}, rootModalDrawer } = props;

  const modelPlain = model ? (toPlainIfImmutable(model) as ScheduledTransactionType) : {};
  const txn = (toPlainIfImmutable(modelPlain.transaction) ?? {}) as Partial<TransactionProps>;
  const incomeCoaIds = useAppSelector(categoriesSelectors.getIncomeCOAIds);
  const derivedIsIncome = modelPlain.transaction
    ? scheduledTransactionsUtils.isIncomeSeriesFromTxn(modelPlain.transaction, incomeCoaIds)
    : false;

  const frequency = modelPlain.recurrence ? customFrequencyUtils.recurrenceRowAlias(modelPlain.recurrence) : undefined;
  const title = modelPlain.recurrence?.alias === 'ONE_TIME_PAYMENT' ? 'New One-time payment' : 'New Series';
  const modelNextDate =
    modelPlain.recurrence && modelPlain.dueOn
      ? customFrequencyUtils
          .recurrenceInfo({ recurrence: modelPlain.recurrence, startDate: DateTime.fromISO(modelPlain.dueOn) })
          ?.nextDates[0]?.toISOString()
      : undefined;
  const nextDue = modelNextDate ? getSmartDateLabel(modelNextDate) : null;

  const exist = Boolean(txn.payee);
  const isDrawer = Boolean(rootModalDrawer);

  if (isDrawer && !exist) {
    return open ? <CreatingSeriesLoading /> : null;
  }

  const content = (
    <div className={isDrawer ? classes.drawerContent : classes.root}>
      <div className={cx(classes.summary, isDrawer && classes.drawerSummary)}>
        <ConnectedAvatar
          kind={getSeriesIconKind(
            Boolean(modelPlain.transaction && transactionsUtils.isRefundTxn(modelPlain.transaction)),
            derivedIsIncome,
          )}
          model={modelPlain}
          size={80}
          isCurrentCategoryIncome={derivedIsIncome}
        />
        {exist ? (
          <Typography variant='headlineRegular' className={classes.name}>
            {txn.payee}
          </Typography>
        ) : (
          <Skeleton variant='rectangular' width={240} height={34} className={classes.name} />
        )}

        {exist ? (
          <>
            <div className={classes.dotRow}>
              <AmountField
                showSign={ShowSignEnum.POSITIVE_ONLY}
                value={txn.amount}
                className={classes.amount}
                id='amount_id'
                variant='bodyMedium'
                editable={false}
              />

              <FiberManualRecordIcon className={classes.dot} />

              <Typography variant='bodyMedium' className={classes.frequency}>
                {frequency}
              </Typography>
            </div>
            {nextDue && (
              <Typography variant='bodyRegular' className={classes.frequency}>
                Next Reminder: {nextDue}
              </Typography>
            )}
          </>
        ) : (
          <Skeleton variant='rectangular' width={280} height={25} />
        )}

        <QSkeleton show={!exist}>
          <Button
            variant='text'
            id='edit-series-button'
            size={isDrawer ? 'large' : 'medium'}
            className={isDrawer ? classes.drawerEditButton : classes.editButton}
            onClick={() => edit()}
          >
            Edit Series
          </Button>
        </QSkeleton>
      </div>

      <QSkeleton show={!exist}>
        <div className={isDrawer ? classes.drawerConnect : undefined}>
          <BillPresentmentConnectBox
            model={model}
            onConnect={onClose}
            onEdit={edit}
            location='create series'
            rootModalDrawer={rootModalDrawer}
          />
        </div>
      </QSkeleton>
    </div>
  );

  if (rootModalDrawer) {
    if (!open) {
      return null;
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <StdDialogTitle variant='drawer' id='dialog-title' onClose={onClose} showCloseButton>
          {title}
        </StdDialogTitle>
        {content}
        <Box className={classes.drawerFooter}>
          <Button variant='outlined' id='close-button' className={classes.drawerCloseButton} onClick={onClose}>
            Close
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <StdDialog onClose={onClose} open={open} size='sm' title='New Recurring'>
      {content}
    </StdDialog>
  );
}

export default ReviewDialog;
